import { c as _c } from "react/compiler-runtime";
import { AvailableValue, VolumeKind } from '@/comps/FilterPicker/FilterPicker';
import { FilterSearchInput } from '@/comps/FilterPicker/FilterSearchInput';
import { FilterOperator } from '@/comps/FilterPicker/filter-operators';
import { formatValue } from '@/comps/FilterPicker/utils';
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { List, ListItem } from '@/comps/list';
import { Button } from '@/comps/primitives/button';
import { Checkbox } from '@/comps/primitives/checkbox';
import { Input } from '@/comps/primitives/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { SortbySelect } from '@/comps/SortBySelect';
import { QueryOrdering } from '@/api/generated/model';
import { translateSafe } from '@/utils/translations';
import { cn } from '@/utils/shad';
import { CheckedState } from '@radix-ui/react-checkbox';
import { Activity, ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react';
import { formatNumber } from '@/utils/format.util';
type Props = {
  filterKind: string;
  operator: FilterOperator;
  containsText: string;
  onContainsTextChange: (text: string) => void;
  onApplyContains: () => void;
  searchFilterValue: string;
  setSearchFilterValue: (v: string) => void;
  sortBy: QueryOrdering;
  onChangeSortBy: (by: QueryOrdering['by']) => void;
  onChangeSortByDirection: (by: QueryOrdering['by']) => void;
  availableFilterValues?: AvailableValue[];
  isLoading: boolean;
  targetFilterValues: string[];
  hasSelectedAll: boolean;
  showSelectAll: boolean;
  onCheckSelectAll: (checked: CheckedState) => void;
  onSelectFilterValue: (value: AvailableValue, checked: CheckedState) => void;
  volumeKind?: VolumeKind;
  onEmptyReset?: () => void;
};
export function FilterValueStep(t0) {
  const $ = _c(56);
  const {
    filterKind,
    operator,
    containsText,
    onContainsTextChange,
    onApplyContains,
    searchFilterValue,
    setSearchFilterValue,
    sortBy,
    onChangeSortBy,
    onChangeSortByDirection,
    availableFilterValues,
    isLoading,
    targetFilterValues,
    hasSelectedAll,
    showSelectAll,
    onCheckSelectAll,
    onSelectFilterValue,
    volumeKind,
    onEmptyReset
  } = t0;
  if (operator === "contains") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="mb-2 px-1 text-[12px] text-muted-foreground">{translateSafe("common", "searchEllipsis")}</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = translateSafe("common", "searchEllipsis");
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== onContainsTextChange) {
      t3 = e => onContainsTextChange(e.currentTarget.value);
      $[2] = onContainsTextChange;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== containsText || $[5] !== onApplyContains) {
      t4 = e_0 => {
        if (e_0.key === "Enter" && containsText.trim()) {
          onApplyContains();
        }
      };
      $[4] = containsText;
      $[5] = onApplyContains;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== containsText || $[8] !== t3 || $[9] !== t4) {
      t5 = <Input placeholder={t2} className="mb-3 w-full rounded border bg-background px-3 text-[12px] focus-visible:ring-0" value={containsText} onChange={t3} onKeyDown={t4} autoFocus={true} />;
      $[7] = containsText;
      $[8] = t3;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== containsText) {
      t6 = containsText.trim();
      $[11] = containsText;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    const t7 = !t6;
    let t8;
    if ($[13] !== onApplyContains || $[14] !== t7) {
      t8 = <Button className="w-full" size="sm" disabled={t7} onClick={onApplyContains}>Apply filter</Button>;
      $[13] = onApplyContains;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t5 || $[17] !== t8) {
      t9 = <div className="z-0 bg-background p-3">{t1}{t5}{t8}</div>;
      $[16] = t5;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  let t1;
  if ($[19] !== availableFilterValues?.length || $[20] !== isLoading || $[21] !== searchFilterValue || $[22] !== setSearchFilterValue) {
    t1 = (availableFilterValues?.length || !!searchFilterValue) && <FilterSearchInput className="mb-2 px-2" search={searchFilterValue} setSearch={v => setSearchFilterValue(v.toLowerCase())} autoFocus={!!searchFilterValue && !isLoading} />;
    $[19] = availableFilterValues?.length;
    $[20] = isLoading;
    $[21] = searchFilterValue;
    $[22] = setSearchFilterValue;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  let t2;
  if ($[24] !== availableFilterValues?.length || $[25] !== onChangeSortBy || $[26] !== onChangeSortByDirection || $[27] !== sortBy) {
    t2 = !!availableFilterValues?.length && <section className="flex items-center justify-between gap-5 pr-2 pl-3"><span className="min-w-max">{translateSafe("common", "sortBy")}</span><div className="flex grow items-center gap-2"><SortbySelect value={sortBy.by} onChange={onChangeSortBy} /><Button className="w-8" variant="outline" onClick={() => onChangeSortByDirection(sortBy.by)}>{sortBy.direction === "desc" ? <ArrowDownWideNarrow /> : <ArrowUpNarrowWide />}</Button></div></section>;
    $[24] = availableFilterValues?.length;
    $[25] = onChangeSortBy;
    $[26] = onChangeSortByDirection;
    $[27] = sortBy;
    $[28] = t2;
  } else {
    t2 = $[28];
  }
  let t3;
  if ($[29] !== t1 || $[30] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[29] = t1;
    $[30] = t2;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  let t4;
  if ($[32] !== hasSelectedAll || $[33] !== onCheckSelectAll || $[34] !== showSelectAll) {
    t4 = showSelectAll && <ListItem className={cn("sticky top-0 flex min-w-67.5 items-center justify-between gap-2 bg-neutral-950/50 border-b px-3 py-2 backdrop-blur-sm hover:cursor-auto hover:bg-inherit")}><div className="flex items-center gap-2"><Checkbox checked={hasSelectedAll} onCheckedChange={onCheckSelectAll} /><span className="font-bold">{translateSafe("common", "selectAll")}</span></div></ListItem>;
    $[32] = hasSelectedAll;
    $[33] = onCheckSelectAll;
    $[34] = showSelectAll;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  let t5;
  if ($[36] !== availableFilterValues?.length || $[37] !== isLoading || $[38] !== onEmptyReset || $[39] !== targetFilterValues) {
    t5 = !isLoading && !targetFilterValues.length && !availableFilterValues?.length && <ListItem className="py-3 text-muted-foreground hover:cursor-auto" onClick={onEmptyReset}>{translateSafe("common", "noDataAvailable")}</ListItem>;
    $[36] = availableFilterValues?.length;
    $[37] = isLoading;
    $[38] = onEmptyReset;
    $[39] = targetFilterValues;
    $[40] = t5;
  } else {
    t5 = $[40];
  }
  let t6;
  if ($[41] !== availableFilterValues || $[42] !== filterKind || $[43] !== hasSelectedAll || $[44] !== onSelectFilterValue || $[45] !== targetFilterValues || $[46] !== volumeKind) {
    t6 = availableFilterValues?.map((availableFilterValue, i) => {
      const checked = targetFilterValues.includes(availableFilterValue.value) || hasSelectedAll || false;
      return <ListItem key={i} className="mx-1 my-0.5 flex w-full min-w-67.5 items-center justify-between rounded-[8px] px-2 py-1.5 transition-colors ease-out hover:cursor-auto"><div className="flex w-full items-center gap-2"><Checkbox id={"filter-value-" + filterKind + i} checked={checked} onCheckedChange={checked_0 => onSelectFilterValue(availableFilterValue, checked_0)} /><Tooltip><TooltipTrigger className="flex-1" asChild={true}><label htmlFor={"filter-value-" + filterKind + i} className="cursor-pointer truncate font-normal text-muted-foreground" title={availableFilterValue.value}>{formatValue(filterKind, availableFilterValue.value)}</label></TooltipTrigger><TooltipContent side="top" className="text-start text-wrap">{formatValue(filterKind, availableFilterValue.value)}</TooltipContent></Tooltip>{volumeKind && !!availableFilterValue.volume && <div className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground"><Activity size={12} /><span>{formatNumber(availableFilterValue.volume)}</span></div>}</div></ListItem>;
    });
    $[41] = availableFilterValues;
    $[42] = filterKind;
    $[43] = hasSelectedAll;
    $[44] = onSelectFilterValue;
    $[45] = targetFilterValues;
    $[46] = volumeKind;
    $[47] = t6;
  } else {
    t6 = $[47];
  }
  let t7;
  if ($[48] !== t4 || $[49] !== t5 || $[50] !== t6) {
    t7 = <List className="text-[12px] leading-4"><div className="relative overflow-y-auto">{t4}{t5}{t6}</div></List>;
    $[48] = t4;
    $[49] = t5;
    $[50] = t6;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  let t8;
  if ($[52] !== isLoading || $[53] !== t3 || $[54] !== t7) {
    t8 = <div className="z-0 mb-2 bg-background"><WithLoadingContainer className="min-h-12.5" loading={isLoading} hideChildren={true}>{t3}{t7}</WithLoadingContainer></div>;
    $[52] = isLoading;
    $[53] = t3;
    $[54] = t7;
    $[55] = t8;
  } else {
    t8 = $[55];
  }
  return t8;
}